/// <reference types="vite/client" />
import { toAscii, tokenize } from "./tokenizer";
import {
  PlantDictionary,
  PlantDictionaryCategory,
  UserDictionaryEntry,
  DictionaryMapping,
  OfflineTrainingLog
} from "../types";
import {
  saveUserDictionary,
  getUserDictionaries,
  getUserDictionariesByIndustry,
  saveDictionaryMapping,
  findDictionaryMapping,
  saveTrainingLog
} from "../offline_storage";

type CategoryKey = UserDictionaryEntry["category"];

const categoryMap: Record<string, CategoryKey> = {
  "1_profil_produkcji_i_procesy": "process",
  "2_park_maszynowy_i_awarie": "equipment",
  "3_surowce_materialy_i_narzedzia": "material",
  "4_bhp_i_bezpieczenstwo_chemiczne": "safety"
};

function slugify(value: string): string {
  return toAscii(value).toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
}

function makeEntryId(industry: string, plantId: string, category: string, term: string): string {
  return `${slugify(industry)}::${slugify(plantId)}::${category}::${slugify(term)}`;
}

function overlapScore(a: string, b: string): number {
  const ta = new Set(tokenize(a));
  const tb = new Set(tokenize(b));
  let common = 0;
  for (const w of ta) if (tb.has(w)) common++;
  return common;
}

function findBestCanonical(slang: string, candidates: string[]): string {
  if (candidates.length === 0) return slang;
  let best = candidates[0];
  let bestScore = overlapScore(slang, candidates[0]);
  for (let i = 1; i < candidates.length; i++) {
    const s = overlapScore(slang, candidates[i]);
    if (s > bestScore) {
      bestScore = s;
      best = candidates[i];
    }
  }
  return bestScore > 0 ? best : slang;
}

function buildOfficialList(cat: PlantDictionaryCategory): string[] {
  return [...(cat.termy_kluczowe ?? []), ...(cat.awarie_i_bledy ?? [])];
}

export async function importPlantDictionary(
  dictionary: PlantDictionary,
  industry: string,
  plantId: string
): Promise<void> {
  const entries: UserDictionaryEntry[] = [];

  for (const [catKey, cat] of Object.entries(dictionary.kategorie)) {
    const baseCategory = categoryMap[catKey] ?? "process";
    const examples = cat.przykladowe_zgloszenia_ai ?? [];
    const officialTerms = cat.termy_kluczowe ?? [];
    const failureTerms = cat.awarie_i_bledy ?? [];
    const slangTerms = cat.zargon_halowy ?? [];
    const allOfficial = buildOfficialList(cat);

    for (const term of officialTerms) {
      entries.push({
        id: makeEntryId(industry, plantId, baseCategory, term),
        industry,
        plantId,
        category: baseCategory,
        term,
        canonical: term,
        synonyms: [],
        rawForms: [],
        examples,
        weight: 1,
        scope: "plant",
        source: "base",
        updatedAt: Date.now()
      });
    }

    for (const term of failureTerms) {
      entries.push({
        id: makeEntryId(industry, plantId, "failure", term),
        industry,
        plantId,
        category: "failure",
        term,
        canonical: term,
        synonyms: [],
        rawForms: [],
        examples,
        weight: 1,
        scope: "plant",
        source: "base",
        updatedAt: Date.now()
      });
    }

    for (const term of slangTerms) {
      const canonical = findBestCanonical(term, allOfficial);
      entries.push({
        id: makeEntryId(industry, plantId, "slang", term),
        industry,
        plantId,
        category: "slang",
        term,
        canonical,
        synonyms: [],
        rawForms: [],
        examples: [],
        weight: 0.8,
        scope: "plant",
        source: "base",
        updatedAt: Date.now()
      });
    }
  }

  for (const entry of entries) {
    await saveUserDictionary(entry);
  }
}

export async function loadBaseDictionaries(): Promise<void> {
  const existing = await getUserDictionaries();
  if (existing.length > 0) return;

  const modules = import.meta.glob(
    "../data/dictionaries/słownik_dla_*.json",
    { eager: true }
  ) as Record<string, { default: PlantDictionary }>;

  for (const [path, mod] of Object.entries(modules)) {
    const match = path.match(/słownik_dla_(\w+)\.json$/);
    const industry = match ? match[1].toLowerCase() : "unknown";
    const dict = mod.default;
    const plantId = dict.zaklad || industry;
    await importPlantDictionary(dict, industry, plantId);
  }
}

export async function getDictionariesForIndustry(
  industry: string,
  plantId?: string,
  managerId?: number
): Promise<UserDictionaryEntry[]> {
  return getUserDictionariesByIndustry(industry, plantId, managerId);
}

export async function recordMapping(
  rawToken: string,
  resolvedCanonical: string,
  category: CategoryKey,
  industry: string,
  plantId: string,
  managerId?: number
): Promise<void> {
  const existing = await findDictionaryMapping(rawToken);
  if (existing && existing.resolvedCanonical === resolvedCanonical) {
    existing.occurrenceCount += 1;
    existing.lastUsedAt = Date.now();
    await saveDictionaryMapping(existing);
    return;
  }

  const id = `${slugify(industry)}::${slugify(plantId)}::${slugify(rawToken)}::${slugify(resolvedCanonical)}`;
  const mapping: DictionaryMapping = {
    id,
    rawToken,
    resolvedCanonical,
    category,
    confidence: 1,
    industry,
    plantId,
    managerId,
    occurrenceCount: 1,
    lastUsedAt: Date.now()
  };
  await saveDictionaryMapping(mapping);
}

export async function recordUnknown(
  transcriptFragment: string,
  unresolvedTokens: string[],
  industry: string,
  plantId: string,
  managerId?: number,
  context?: string
): Promise<void> {
  if (unresolvedTokens.length === 0) return;
  const id = `${slugify(industry)}::${slugify(plantId)}::${Date.now()}::${unresolvedTokens.map(slugify).join("_")}`;
  const entry: OfflineTrainingLog = {
    id,
    transcriptFragment,
    unresolvedTokens,
    context,
    industry,
    plantId,
    managerId,
    createdAt: Date.now(),
    synced: false
  };
  await saveTrainingLog(entry);
}

// Zapisuje korektę kategorii incydentu (Awaria/BHP/Jakość/Braki/Serwis).
// Kategoria nie jest polem słownikowym (nie pochodzi z dopasowań entity_extractor),
// więc trafia tylko do logu treningowego — do ręcznej kuracji listy słów kluczowych
// kategorii (detectIncidentCategory) po synchronizacji.
export async function recordCategoryCorrection(
  originalCategory: string,
  correctedCategory: string,
  industry: string,
  plantId: string,
  managerId?: number,
  context?: string
): Promise<void> {
  if (!correctedCategory.trim() || originalCategory.trim() === correctedCategory.trim()) return;
  const id = `${slugify(industry)}::${slugify(plantId)}::category::${Date.now()}::${slugify(originalCategory)}`;
  const entry: OfflineTrainingLog = {
    id,
    transcriptFragment: originalCategory,
    unresolvedTokens: [],
    userCorrection: correctedCategory,
    context: context ? `kategoria: ${context}` : "kategoria",
    industry,
    plantId,
    managerId,
    createdAt: Date.now(),
    synced: false
  };
  await saveTrainingLog(entry);
}

// Zapisuje ręczną poprawkę użytkownika (np. zmiana kategorii/obiektu/typu na E2b/E3b).
// 1) loguje korektę w offline_training_log (do wglądu/kuracji słownika po synchronizacji),
// 2) zapisuje nauczone mapowanie w user_dictionaries (scope: "user"), żeby ta sama fraza
//    była poprawnie rozwiązywana już przy najbliższym lokalnym dyktowaniu tego managera.
export async function recordCorrection(
  category: CategoryKey,
  originalValue: string,
  correctedValue: string,
  industry: string,
  plantId: string,
  managerId?: number,
  context?: string
): Promise<void> {
  if (!correctedValue.trim() || originalValue.trim() === correctedValue.trim()) return;

  const id = `${slugify(industry)}::${slugify(plantId)}::${Date.now()}::${slugify(originalValue)}`;
  const entry: OfflineTrainingLog = {
    id,
    transcriptFragment: originalValue,
    unresolvedTokens: [],
    userCorrection: correctedValue,
    context,
    industry,
    plantId,
    managerId,
    createdAt: Date.now(),
    synced: false
  };
  await saveTrainingLog(entry);

  if (!originalValue.trim()) return;

  const learnedId = makeEntryId(industry, `${plantId}::user::${managerId ?? "anon"}`, category, originalValue);
  const learnedEntry: UserDictionaryEntry = {
    id: learnedId,
    industry,
    plantId,
    category,
    term: originalValue.trim(),
    canonical: correctedValue.trim(),
    synonyms: [],
    rawForms: [],
    weight: 1.5,
    scope: "user",
    managerId,
    source: "user",
    updatedAt: Date.now()
  };
  await saveUserDictionary(learnedEntry);
}
